import React, { useEffect, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useField } from 'formik';
import { CalendarIcon } from '@heroicons/react/24/outline';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import useSearchInput from '@/dashboard/hooks/useSearchInput';
import getProductPriceLabel from '@dashboard/services/translation/getProductPriceLabel';
import { pageKey } from '@dashboard/lib/queryKeys';
import { formatEventDate } from '@/dashboard/lib/format-date';

import { Badge } from '@dashboard/components/ui/badge';
import { Label } from '@/dashboard/components/Form';
import SearchSelect from '@dashboard/components/SearchSelect';

const PageOption = ({ page }) => (
  <>
    <div className="text-xs text-muted-foreground capitalize">{page.type}</div>
    <div className="truncate text-sm font-semibold">{page.name}</div>
    {page.type === 'checkout' && (
      <div className="text-sm text-muted-foreground">
        <span>{getProductPriceLabel(page.product)}</span>
        {/* TODO(M14): reads legacy product scalars - repoint to prices[]/selected price before legacy fields are removed (product.productId/planId) */}
        {page.product?.productId != null && page.product?.planId != null && (
          <div className="space-x-1">
            <Badge variant="outline">{page.product.productId}</Badge>
            <Badge variant="outline">{page.product.planId}</Badge>
          </div>
        )}
      </div>
    )}
    {page.type === 'event' && page.eventDetails?.startDate && (
      <div className="text-xs text-muted-foreground">
        <div className="flex items-start gap-x-1.5">
          <CalendarIcon className="mt-px !size-3.5 shrink-0" />
          <div className="flex flex-col">
            {formatEventDate({
              startDate: page.eventDetails.startDate,
              endDate: page.eventDetails.endDate,
              timezone: page.eventDetails.timezone,
              locale: page.locale,
            }).map(date => (
              <span key={date}>{date}</span>
            ))}
          </div>
        </div>
      </div>
    )}
  </>
);

const PageSelect = ({ showLabel = false, name, pageTypes = ['checkout', 'event', 'form'] }) => {
  const [formikField, , formikHelpers] = useField(name);
  const [selectedPage, setSelectedPage] = useState();
  const { searchInput, setSearchInput, search } = useSearchInput('');

  const { getPages, getPage } = usePageQueries();
  const { sellerId } = useAuth();

  const { data: currentPageRedirect } = useQuery({
    queryKey: pageKey(formikField.value),
    queryFn: () => getPage(formikField.value),
    enabled: !!formikField.value,
  });

  useEffect(() => {
    if (!selectedPage && currentPageRedirect) {
      setSelectedPage(currentPageRedirect);
      setSearchInput(currentPageRedirect.name);
    }
  }, [selectedPage, currentPageRedirect, setSearchInput]);

  useEffect(() => {
    if (!selectedPage) return;
    if (selectedPage._id !== formikField.value) {
      formikHelpers.setValue(selectedPage._id);
    }
  }, [selectedPage]);

  const effectiveSearch = selectedPage || searchInput === '' ? '' : search;

  const { data, fetchNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: ['pages', effectiveSearch, pageTypes],
    queryFn: async ({ pageParam }) =>
      await getPages(sellerId, {
        status: ['published', 'draft'],
        type: pageTypes,
        limit: 10,
        skip: pageParam * 10,
        search: effectiveSearch === '' ? undefined : effectiveSearch,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
  });

  const pageGroups = data?.pages?.map(group => group?.data ?? []) ?? [];

  return (
    <div>
      {showLabel && (
        <Label htmlFor={name} tooltip="Display a new page after checkout or submission">
          Next page
        </Label>
      )}

      <SearchSelect
        name={name}
        inputValue={searchInput ?? ''}
        onInputChange={value => {
          setSearchInput(value);
          if (selectedPage) setSelectedPage(null);
        }}
        placeholder="Search for a page"
        ariaLabel="Search for a page"
        hasSelection={!!selectedPage}
        onClear={() => {
          setSelectedPage(null);
          formikHelpers.setValue(null);
          setSearchInput('');
        }}
        itemPages={pageGroups}
        getItemKey={page => page._id}
        selectedKey={selectedPage?._id}
        onSelect={page => {
          setSelectedPage(page);
          formikHelpers.setValue(page._id);
          setSearchInput(page.name);
        }}
        renderItem={page => <PageOption page={page} />}
        getItemUrl={page => `/dashboard/pages/${page._id}/customize/product`}
        openItemLabel="Open page in new tab"
        isLoading={isLoading}
        isError={isError}
        errorMessage="Error loading pages"
        emptyMessage="No pages found"
        onLoadMore={fetchNextPage}
      />
    </div>
  );
};

export default PageSelect;
